import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Mesh, BoxGeometry, EdgesGeometry, LineSegments } from 'three';
import { Text } from '@react-three/drei';
import { PCCase } from '../../types';

interface CaseModelProps {
  pcCase: PCCase;
  position?: [number, number, number];
  color?: string;
  showLabels?: boolean;
  showDimensions?: boolean;
  scale?: number;
  autoRotate?: boolean;
  opacity?: number;
}

export const CaseModel: React.FC<CaseModelProps> = ({
  pcCase,
  position = [0, 0, 0],
  color = '#E8590C',
  showLabels = true,
  showDimensions = false,
  scale = 0.005,
  autoRotate = false,
  opacity = 1,
}) => {
  const meshRef = useRef<Mesh>(null);
  const edgesRef = useRef<LineSegments>(null);

  // Convert mm dimensions to scene units
  const width = pcCase.dimensions.width * scale;
  const height = pcCase.dimensions.height * scale;
  const depth = pcCase.dimensions.depth * scale;

  useFrame((_, delta) => {
    if (autoRotate && meshRef.current) {
      meshRef.current.rotation.y += delta * 0.3;
    }
  });

  const edgesGeometry = useMemo(() => {
    const boxGeo = new BoxGeometry(width, height, depth);
    return new EdgesGeometry(boxGeo);
  }, [width, height, depth]);

  return (
    <group position={position}>
      {/* Main case body */}
      <mesh ref={meshRef} castShadow receiveShadow>
        <boxGeometry args={[width, height, depth]} />
        <meshStandardMaterial
          color={color}
          transparent
          opacity={opacity * 0.3}
          roughness={0.7}
          metalness={0.3}
        />
      </mesh>

      {/* Edges for better visibility */}
      <lineSegments ref={edgesRef} geometry={edgesGeometry}>
        <lineBasicMaterial color={color} linewidth={2} transparent opacity={opacity} />
      </lineSegments>

      {/* Front panel detail */}
      <mesh position={[0, height * 0.15, depth / 2 + 0.001]}>
        <planeGeometry args={[width * 0.8, height * 0.3]} />
        <meshStandardMaterial
          color={color}
          transparent
          opacity={opacity * 0.5}
        />
      </mesh>

      {/* Power button */}
      <mesh position={[width * 0.3, height * 0.4, depth / 2 + 0.002]}>
        <circleGeometry args={[width * 0.05, 16]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>

      {/* Label */}
      {showLabels && (
        <Text
          position={[0, height / 2 + 0.15, 0]}
          fontSize={0.12}
          color="white"
          anchorX="center"
          anchorY="bottom"
        >
          {pcCase.name}
        </Text>
      )}

      {/* Dimension labels */}
      {showDimensions && (
        <>
          {/* Height */}
          <Text
            position={[-width / 2 - 0.15, 0, 0]}
            fontSize={0.08}
            color="#8A8A92"
            anchorX="right"
            anchorY="middle"
            rotation={[0, 0, Math.PI / 2]}
          >
            {pcCase.dimensions.height}mm
          </Text>

          {/* Width */}
          <Text
            position={[0, -height / 2 - 0.1, depth / 2]}
            fontSize={0.08}
            color="#8A8A92"
            anchorX="center"
            anchorY="top"
          >
            {pcCase.dimensions.width}mm
          </Text>

          {/* Depth */}
          <Text
            position={[width / 2 + 0.15, -height / 2 - 0.1, 0]}
            fontSize={0.08}
            color="#8A8A92"
            anchorX="left"
            anchorY="top"
            rotation={[0, Math.PI / 2, 0]}
          >
            {pcCase.dimensions.depth}mm
          </Text>
        </>
      )}
    </group>
  );
};
